/**
 * Bedzy Premium Theme — Main JavaScript
 * Handles: overlays, predictive search, quick view, cart drawer,
 * product variants, quiz, tabs, animations, and more.
 */

(function () {
  'use strict';

  // ==========================================
  // UTILITIES
  // ==========================================
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = parseFloat(cents.replace(/[^0-9.]/g, '')) * 100;
    return '£' + (cents / 100).toFixed(2);
  }

  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(r => {
      if (!r.ok) throw new Error(r.statusText);
      return r.json();
    });
  }

  // ==========================================
  // OVERLAY SYSTEM
  // ==========================================
  function initOverlays() {
    // Toggle buttons
    document.querySelectorAll('[data-overlay-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const target = btn.getAttribute('data-overlay-toggle');
        openOverlay(target);
      });
    });

    // Close buttons & backdrops
    document.querySelectorAll('[data-overlay-close]').forEach(el => {
      el.addEventListener('click', () => {
        const target = el.getAttribute('data-overlay-close');
        closeOverlay(target);
      });
    });

    // ESC key closes all overlays
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAllOverlays();
    });
  }

  function openOverlay(name) {
    const overlay = document.getElementById(getOverlayId(name));
    if (!overlay) return;
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overlay-open');

    // Focus search input when search opens
    if (name === 'search') {
      setTimeout(() => {
        const input = document.getElementById('PredictiveSearchInput');
        if (input) input.focus();
      }, 300);
    }

    // Refresh cart when cart opens
    if (name === 'cart') {
      refreshCartDrawer();
    }
  }

  function closeOverlay(name) {
    const overlay = document.getElementById(getOverlayId(name));
    if (!overlay) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overlay-open');
  }

  function closeAllOverlays() {
    document.querySelectorAll('.overlay.is-open').forEach(o => {
      o.classList.remove('is-open');
      o.setAttribute('aria-hidden', 'true');
    });
    document.body.classList.remove('overlay-open');
  }

  function getOverlayId(name) {
    const map = {
      'search': 'SearchOverlay',
      'quickview': 'QuickViewModal',
      'cart': 'CartDrawer',
      'mobile-menu': 'MobileMenu'
    };
    return map[name] || name;
  }

  // ==========================================
  // HEADER SCROLL — REMOVED (header is always transparent, never sticky)
  // ==========================================

  // ==========================================
  // PREDICTIVE SEARCH
  // ==========================================
  function initPredictiveSearch() {
    const input = document.getElementById('PredictiveSearchInput');
    const resultsContainer = document.getElementById('PredictiveSearchResults');
    if (!input || !resultsContainer) return;

    const searchUrl = window.Bedzy?.predictiveSearchUrl;
    if (!searchUrl) return;

    const handleSearch = debounce(async (query) => {
      if (query.length < 2) {
        resultsContainer.innerHTML = '<div style="text-align:center;padding:40px;color:var(--color-text-muted);">Start typing to search...</div>';
        return;
      }

      resultsContainer.innerHTML = '<div style="text-align:center;padding:40px;color:var(--color-text-muted);">Searching...</div>';

      try {
        const url = `${searchUrl}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&section_id=predictive-search`;
        const response = await fetch(url);
        const text = await response.text();

        // Extract section content
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const section = doc.getElementById('shopify-section-predictive-search');
        resultsContainer.innerHTML = section ? section.innerHTML : '<div style="text-align:center;padding:40px;">No results found</div>';
      } catch (err) {
        resultsContainer.innerHTML = '<div style="text-align:center;padding:40px;color:var(--color-text-muted);">Search unavailable</div>';
      }
    }, 300);

    input.addEventListener('input', () => handleSearch(input.value.trim()));
  }

  // ==========================================
  // QUICK VIEW
  // ==========================================
  function initQuickView() {
    document.addEventListener('click', async (e) => {
      const trigger = e.target.closest('[data-quickview-handle]');
      if (!trigger) return;

      e.preventDefault();
      const handle = trigger.getAttribute('data-quickview-handle');
      const container = document.getElementById('QuickViewContent');
      if (!container) return;

      container.innerHTML = '<div style="grid-column:1/-1;text-align:center;padding:40px;">Loading...</div>';
      openOverlay('quickview');

      try {
        const product = await fetchJSON(`/products/${handle}.js`);
        const image = product.featured_image || '';
        const price = formatMoney(product.price);
        const comparePrice = product.compare_at_price > product.price ? formatMoney(product.compare_at_price) : '';
        const desc = product.description ? product.description.replace(/<[^>]*>/g, '').substring(0, 200) + '...' : '';

        container.innerHTML = `
          <div class="quickview-content__image">
            ${image ? `<img src="${image}" alt="${product.title}" width="500">` : '<div style="width:100%;height:100%;background:var(--color-bg-cream);"></div>'}
          </div>
          <div style="padding:20px 0;">
            <div class="product-info__vendor">${product.vendor || ''}</div>
            <h3 style="margin-bottom:12px;">${product.title}</h3>
            <div class="product-info__price" style="margin-bottom:16px;">
              <span class="price${comparePrice ? ' price--sale' : ''}">${price}</span>
              ${comparePrice ? `<span class="price price--compare">${comparePrice}</span>` : ''}
            </div>
            <p style="color:var(--color-text-light);font-size:0.9375rem;margin-bottom:24px;">${desc}</p>
            <a href="/products/${handle}" class="btn btn--primary btn--full">View Full Details</a>
            <button class="btn btn--outline-gold btn--full" style="margin-top:8px;" data-quickview-add="${product.variants[0].id}">Add to Bag — ${price}</button>
          </div>
        `;

        // Add to cart from quick view
        const addBtn = container.querySelector('[data-quickview-add]');
        if (addBtn) {
          addBtn.addEventListener('click', async () => {
            const variantId = addBtn.getAttribute('data-quickview-add');
            await addToCart(variantId, 1);
            closeOverlay('quickview');
            openOverlay('cart');
          });
        }
      } catch (err) {
        container.innerHTML = '<div style="grid-column:1/-1;text-align:center;padding:40px;">Could not load product</div>';
      }
    });
  }

  // ==========================================
  // CART DRAWER
  // ==========================================
  async function refreshCartDrawer() {
    try {
      const cart = await fetchJSON('/cart.js');
      renderCartDrawer(cart);
      updateCartCount(cart.item_count);
    } catch (err) {
      console.error('Cart refresh error:', err);
    }
  }

  function renderCartDrawer(cart) {
    const itemsContainer = document.getElementById('CartDrawerItems');
    const subtotalEl = document.getElementById('CartDrawerSubtotal');
    const footerEl = document.getElementById('CartDrawerFooter');
    const progressFill = document.getElementById('ShippingProgressFill');
    const progressText = document.getElementById('ShippingProgressText');

    if (!itemsContainer) return;

    const threshold = window.Bedzy?.freeShippingThreshold || 300;
    const subtotalPounds = cart.total_price / 100;
    const progress = Math.min((subtotalPounds / threshold) * 100, 100);

    if (progressFill) progressFill.style.width = progress + '%';
    if (progressText) {
      if (subtotalPounds >= threshold) {
        progressText.innerHTML = '🎉 <strong>You\'ve unlocked free delivery!</strong>';
        progressText.classList.add('shipping-progress__text--success');
      } else {
        const remaining = (threshold - subtotalPounds).toFixed(2);
        progressText.innerHTML = `Add <strong>£${remaining}</strong> more for free delivery`;
        progressText.classList.remove('shipping-progress__text--success');
      }
    }

    if (cart.items.length === 0) {
      itemsContainer.innerHTML = `
        <div class="cart-drawer__empty">
          <div class="cart-drawer__empty-icon">🛏️</div>
          <div class="cart-drawer__empty-title">Your bag is empty</div>
          <div class="cart-drawer__empty-text">Start shopping and discover your perfect bed.</div>
          <a href="/collections" class="btn btn--primary">Shop Now</a>
        </div>
      `;
      if (footerEl) footerEl.style.display = 'none';
      return;
    }

    if (footerEl) footerEl.style.display = 'block';

    let html = '';
    cart.items.forEach(item => {
      const image = item.image ? item.image.replace(/(\.[^.]+)$/, '_100x100$1') : '';
      html += `
        <div class="cart-item" data-line-key="${item.key}">
          <div class="cart-item__image">
            ${image ? `<img src="${image}" alt="${item.title}" width="72" height="72">` : ''}
          </div>
          <div>
            <div class="cart-item__title">${item.product_title}</div>
            <div class="cart-item__variant">${item.variant_title || ''}</div>
            <div class="cart-item__qty">
              <button data-cart-qty-change="${item.key}" data-delta="-1">−</button>
              <span>${item.quantity}</span>
              <button data-cart-qty-change="${item.key}" data-delta="1">+</button>
            </div>
            <span class="cart-item__remove" data-cart-remove="${item.key}">Remove</span>
          </div>
          <div class="cart-item__price">${formatMoney(item.final_line_price)}</div>
        </div>
      `;
    });

    itemsContainer.innerHTML = html;
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    // Quantity change listeners
    itemsContainer.querySelectorAll('[data-cart-qty-change]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.getAttribute('data-cart-qty-change');
        const delta = parseInt(btn.getAttribute('data-delta'));
        const item = cart.items.find(i => i.key === key);
        if (!item) return;
        const newQty = Math.max(0, item.quantity + delta);
        await updateCartItem(key, newQty);
      });
    });

    // Remove listeners
    itemsContainer.querySelectorAll('[data-cart-remove]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.getAttribute('data-cart-remove');
        await updateCartItem(key, 0);
      });
    });
  }

  async function addToCart(variantId, quantity) {
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId), quantity: quantity || 1 })
      });
      await refreshCartDrawer();
      document.dispatchEvent(new CustomEvent('bedzy:cart-updated'));
    } catch (err) {
      console.error('Add to cart error:', err);
    }
  }

  async function updateCartItem(key, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      await refreshCartDrawer();
      document.dispatchEvent(new CustomEvent('bedzy:cart-updated'));
    } catch (err) {
      console.error('Cart update error:', err);
    }
  }

  function updateCartCount(count) {
    const el = document.getElementById('CartCount');
    if (el) el.textContent = count > 0 ? count : '';
  }

  // ==========================================
  // PRODUCT PAGE
  // ==========================================
  function initProductPage() {
    const productJSON = document.getElementById('ProductJSON');
    if (!productJSON) return;

    let product;
    try { product = JSON.parse(productJSON.textContent); } catch (e) { return; }

    const select = document.getElementById('ProductSelect');
    const addBtn = document.getElementById('AddToCart');
    if (!select) return;

    // --- Variant option buttons ---
    document.querySelectorAll('.size-option, .color-option').forEach(btn => {
      btn.addEventListener('click', () => {
        const parent = btn.closest('.size-options, .color-options');
        parent.querySelectorAll('.size-option, .color-option').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        const optionIndex = parent.getAttribute('data-option-index');
        const label = document.getElementById('SelectedOption' + (parseInt(optionIndex) + 1));
        if (label) label.textContent = btn.getAttribute('data-value');

        updateSelectedVariant(product, select, addBtn);
      });
    });

    // --- Quantity buttons ---
    const qtyInput = document.querySelector('[data-qty-input]');
    document.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
      if (qtyInput && parseInt(qtyInput.value) > 1) qtyInput.value = parseInt(qtyInput.value) - 1;
    });
    document.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
      if (qtyInput && parseInt(qtyInput.value) < 10) qtyInput.value = parseInt(qtyInput.value) + 1;
    });

    // --- Image thumbnails ---
    document.querySelectorAll('.product-gallery__thumb').forEach(thumb => {
      thumb.addEventListener('click', () => {
        document.querySelectorAll('.product-gallery__thumb').forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
        const mainImg = document.getElementById('ProductImage');
        if (mainImg) {
          mainImg.src = thumb.getAttribute('data-image-url');
          mainImg.alt = thumb.getAttribute('data-image-alt') || '';
          mainImg.setAttribute('data-zoom-src', thumb.getAttribute('data-zoom-url') || thumb.getAttribute('data-image-url'));
        }
      });
    });

    // --- Hover zoom on desktop ---
    initImageZoom();

    // --- Lightbox ---
    initLightbox(product);

    // --- Touch swipe gallery on mobile ---
    initGallerySwipe(product);

    // --- Sticky mobile add-to-cart bar ---
    initStickyBar(product, select);

    // --- Review link scroll ---
    document.querySelectorAll('[data-scroll-tab]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const tabName = link.getAttribute('data-scroll-tab');
        const tab = document.querySelector(`.product-tabs__tab[data-tab="${tabName}"]`);
        if (tab) {
          tab.click();
          tab.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });

    // --- AJAX add to cart (enhanced with animation) ---
    const form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const formData = new FormData(form);
        const variantId = formData.get('id');
        const quantity = formData.get('quantity') || 1;

        if (addBtn) {
          addBtn.disabled = true;
          addBtn.classList.add('is-loading');
          addBtn.classList.remove('is-added');
        }

        await addToCart(variantId, parseInt(quantity));

        if (addBtn) {
          addBtn.classList.remove('is-loading');
          addBtn.classList.add('is-added');
          setTimeout(() => {
            addBtn.classList.remove('is-added');
            addBtn.disabled = false;
          }, 2000);
        }

        openOverlay('cart');
      });
    }
  }

  // --- Image Hover Zoom ---
  function initImageZoom() {
    const zoomWrap = document.querySelector('[data-zoom-wrap]');
    if (!zoomWrap) return;
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (isMobile) return;

    let isZoomed = false;

    zoomWrap.addEventListener('click', (e) => {
      if (!isZoomed) {
        isZoomed = true;
        zoomWrap.classList.add('is-zoomed');
        updateZoomPosition(e);
      } else {
        isZoomed = false;
        zoomWrap.classList.remove('is-zoomed');
        const img = zoomWrap.querySelector('img');
        if (img) img.style.transformOrigin = 'center center';
      }
    });

    zoomWrap.addEventListener('mousemove', (e) => {
      if (isZoomed) updateZoomPosition(e);
    });

    zoomWrap.addEventListener('mouseleave', () => {
      if (isZoomed) {
        isZoomed = false;
        zoomWrap.classList.remove('is-zoomed');
        const img = zoomWrap.querySelector('img');
        if (img) img.style.transformOrigin = 'center center';
      }
    });

    function updateZoomPosition(e) {
      const rect = zoomWrap.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      const img = zoomWrap.querySelector('img');
      if (img) img.style.transformOrigin = x + '% ' + y + '%';
    }
  }

  // --- Lightbox ---
  function initLightbox(product) {
    const lightbox = document.getElementById('ProductLightbox');
    if (!lightbox) return;

    const images = product.images || [];
    if (images.length === 0) return;

    let currentIndex = 0;
    const lbImg = document.getElementById('LightboxImage');
    const lbCurrent = lightbox.querySelector('[data-lightbox-current]');

    function openLightbox(index) {
      currentIndex = index || 0;
      updateLightboxImage();
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function closeLightbox() {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    function updateLightboxImage() {
      if (!images[currentIndex]) return;
      const src = images[currentIndex].src ? images[currentIndex].src.replace(/\?.*/, '') + '?width=1600' : '';
      if (lbImg) lbImg.src = src;
      if (lbCurrent) lbCurrent.textContent = currentIndex + 1;
    }

    // Open from fullscreen button or main image click
    document.querySelector('[data-lightbox-open]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      const activeThumb = document.querySelector('.product-gallery__thumb.active');
      const idx = activeThumb ? parseInt(activeThumb.getAttribute('data-image-index')) : 0;
      openLightbox(idx);
    });

    // Close
    lightbox.querySelector('[data-lightbox-close]')?.addEventListener('click', closeLightbox);
    lightbox.addEventListener('click', (e) => {
      if (e.target === lightbox) closeLightbox();
    });

    // Nav
    lightbox.querySelector('[data-lightbox-prev]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      currentIndex = (currentIndex - 1 + images.length) % images.length;
      updateLightboxImage();
    });
    lightbox.querySelector('[data-lightbox-next]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      currentIndex = (currentIndex + 1) % images.length;
      updateLightboxImage();
    });

    // Keyboard nav
    document.addEventListener('keydown', (e) => {
      if (!lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') { currentIndex = (currentIndex - 1 + images.length) % images.length; updateLightboxImage(); }
      if (e.key === 'ArrowRight') { currentIndex = (currentIndex + 1) % images.length; updateLightboxImage(); }
    });
  }

  // --- Touch Swipe Gallery (mobile) ---
  function initGallerySwipe(product) {
    const mainWrap = document.querySelector('[data-zoom-wrap]');
    if (!mainWrap) return;

    const images = product.images || [];
    if (images.length <= 1) return;

    let startX = 0, diffX = 0, currentIdx = 0;

    mainWrap.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      diffX = 0;
    }, { passive: true });

    mainWrap.addEventListener('touchmove', (e) => {
      diffX = e.touches[0].clientX - startX;
    }, { passive: true });

    mainWrap.addEventListener('touchend', () => {
      if (Math.abs(diffX) < 50) return;
      if (diffX < 0 && currentIdx < images.length - 1) currentIdx++;
      else if (diffX > 0 && currentIdx > 0) currentIdx--;
      else return;

      const mainImg = document.getElementById('ProductImage');
      const img = images[currentIdx];
      if (mainImg && img) {
        mainImg.src = img.src ? img.src.replace(/\?.*/, '') + '?width=1200' : '';
      }
      // Update thumb active state
      document.querySelectorAll('.product-gallery__thumb').forEach((t, i) => {
        t.classList.toggle('active', i === currentIdx);
      });
    });
  }

  // --- Sticky Mobile Add-to-Cart Bar ---
  function initStickyBar(product, select) {
    const stickyBar = document.getElementById('StickyBar');
    const addBtn = document.getElementById('AddToCart');
    if (!stickyBar || !addBtn) return;

    // Show/hide based on scroll past add-to-cart button
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
      });
    }, { threshold: 0 });

    observer.observe(addBtn);

    // Sticky bar add-to-cart mirrors main button
    const stickyAddBtn = stickyBar.querySelector('[data-sticky-add]');
    if (stickyAddBtn) {
      stickyAddBtn.addEventListener('click', () => {
        const form = document.querySelector('form[action*="/cart/add"]');
        if (form) {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      });
    }
  }

  function updateSelectedVariant(product, select, addBtn) {
    // Build selected options
    const selectedOptions = [];
    document.querySelectorAll('.size-options, .color-options, [data-option-index]').forEach(group => {
      const active = group.querySelector('.active');
      if (active) selectedOptions.push(active.getAttribute('data-value'));
    });

    // Find matching variant
    const variant = product.variants.find(v => {
      return v.options.every((opt, i) => opt === selectedOptions[i]);
    });

    if (variant) {
      select.value = variant.id;

      // Update price
      const priceEl = document.querySelector('[data-product-price]');
      const compareEl = document.querySelector('[data-compare-price]');
      const addPriceEl = document.querySelector('[data-add-price]');
      const stickyPriceEl = document.querySelector('[data-sticky-price]');

      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (addPriceEl) addPriceEl.textContent = formatMoney(variant.price);
      if (stickyPriceEl) stickyPriceEl.textContent = formatMoney(variant.price);

      if (compareEl && variant.compare_at_price > variant.price) {
        compareEl.innerHTML = '<s>' + formatMoney(variant.compare_at_price) + '</s>';
        compareEl.style.display = '';
      } else if (compareEl) {
        compareEl.style.display = 'none';
      }

      // Update save badge
      const badge = document.querySelector('.badge--sale');
      if (badge) {
        if (variant.compare_at_price > variant.price) {
          badge.textContent = 'Save ' + formatMoney(variant.compare_at_price - variant.price);
          badge.style.display = '';
        } else {
          badge.style.display = 'none';
        }
      }

      // Update image
      if (variant.featured_image) {
        const mainImg = document.getElementById('ProductImage');
        if (mainImg) {
          mainImg.src = variant.featured_image.src;
          const zoomSrc = variant.featured_image.src.replace(/width=\d+/, 'width=2400');
          mainImg.setAttribute('data-zoom-src', zoomSrc);
        }
        // Highlight matching thumbnail
        document.querySelectorAll('.product-gallery__thumb').forEach(t => {
          const thumbUrl = t.getAttribute('data-image-url');
          if (thumbUrl && variant.featured_image.src && thumbUrl.includes(variant.featured_image.src.split('?')[0].split('/').pop())) {
            document.querySelectorAll('.product-gallery__thumb').forEach(tt => tt.classList.remove('active'));
            t.classList.add('active');
          }
        });
      }

      // Update SKU
      const skuEl = document.querySelector('[data-variant-sku]');
      if (skuEl) skuEl.textContent = variant.sku || 'N/A';

      // Update button
      if (addBtn) {
        if (variant.available) {
          addBtn.disabled = false;
          addBtn.classList.remove('is-loading', 'is-added');
        } else {
          addBtn.disabled = true;
        }
      }

      // Update sticky bar button
      const stickyAddBtn = document.querySelector('[data-sticky-add]');
      if (stickyAddBtn) {
        stickyAddBtn.disabled = !variant.available;
        stickyAddBtn.textContent = variant.available ? 'Add to Bag' : 'Sold Out';
      }

      // Update URL without reload
      if (history.replaceState) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
    }
  }

  // ==========================================
  // PRODUCT TABS / ACCORDION
  // ==========================================
  function initProductTabs() {
    // Desktop tabs
    document.querySelectorAll('.product-tabs__tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const target = tab.getAttribute('data-tab');
        document.querySelectorAll('.product-tabs__tab').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        document.querySelectorAll('.product-tabs__panel').forEach(p => p.classList.remove('active'));
        const panel = document.querySelector(`[data-tab-panel="${target}"]`);
        if (panel) panel.classList.add('active');
      });
    });

    // Mobile accordion
    document.querySelectorAll('.product-accordion__header').forEach(header => {
      header.addEventListener('click', () => {
        const panel = header.closest('.product-tabs__panel');
        const isOpen = panel.classList.contains('accordion-open');

        // Close all
        document.querySelectorAll('.product-tabs__panel').forEach(p => p.classList.remove('accordion-open'));
        document.querySelectorAll('.product-accordion__header').forEach(h => h.classList.remove('active'));

        // Toggle clicked
        if (!isOpen) {
          panel.classList.add('accordion-open');
          header.classList.add('active');
        }
      });
    });
  }

  // ==========================================
  // MATTRESS QUIZ
  // ==========================================
  function initQuiz() {
    const quizContainer = document.getElementById('QuizSteps');
    if (!quizContainer) return;

    let currentStep = 1;
    const answers = {};

    // Option selection
    quizContainer.addEventListener('click', (e) => {
      const option = e.target.closest('.quiz-option');
      if (!option) return;

      const step = option.closest('[data-quiz-step]');
      step.querySelectorAll('.quiz-option').forEach(o => o.classList.remove('selected'));
      option.classList.add('selected');
      answers[currentStep] = option.getAttribute('data-value');

      // Enable next button
      const nextBtn = step.querySelector('[data-quiz-next]');
      if (nextBtn) nextBtn.disabled = false;
    });

    // Next button
    quizContainer.addEventListener('click', (e) => {
      if (e.target.closest('[data-quiz-next]')) {
        const totalSteps = quizContainer.querySelectorAll('[data-quiz-step]:not([data-quiz-step="results"])').length;

        if (currentStep >= totalSteps) {
          // Show results
          showStep('results');
          updateProgress(totalSteps, totalSteps);
        } else {
          currentStep++;
          showStep(currentStep);
          updateProgress(currentStep, totalSteps);
        }
      }
    });

    // Previous button
    quizContainer.addEventListener('click', (e) => {
      if (e.target.closest('[data-quiz-prev]')) {
        currentStep--;
        showStep(currentStep);
        const totalSteps = quizContainer.querySelectorAll('[data-quiz-step]:not([data-quiz-step="results"])').length;
        updateProgress(currentStep, totalSteps);
      }
    });

    // Restart
    quizContainer.addEventListener('click', (e) => {
      if (e.target.closest('[data-quiz-restart]')) {
        currentStep = 1;
        Object.keys(answers).forEach(k => delete answers[k]);
        quizContainer.querySelectorAll('.quiz-option').forEach(o => o.classList.remove('selected'));
        quizContainer.querySelectorAll('[data-quiz-next]').forEach(b => b.disabled = true);
        showStep(1);
        const totalSteps = quizContainer.querySelectorAll('[data-quiz-step]:not([data-quiz-step="results"])').length;
        updateProgress(1, totalSteps);
      }
    });

    function showStep(step) {
      quizContainer.querySelectorAll('[data-quiz-step]').forEach(s => s.style.display = 'none');
      const target = quizContainer.querySelector(`[data-quiz-step="${step}"]`);
      if (target) target.style.display = '';
    }

    function updateProgress(current, total) {
      document.querySelectorAll('.quiz-progress__step').forEach((step, i) => {
        step.classList.remove('active', 'completed');
        if (i + 1 < current) step.classList.add('completed');
        else if (i + 1 === current) step.classList.add('active');
      });
    }
  }

  // ==========================================
  // SCROLL ANIMATIONS
  // ==========================================
  function initAnimations() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    document.querySelectorAll('.fade-in, .stagger-in').forEach(el => observer.observe(el));
  }

  // ==========================================
  // MOBILE MENU
  // ==========================================
  function initMobileMenu() {
    const nav = document.getElementById('MobileMenuNav');
    if (!nav) return;

    // Find the main header navigation (single .header__nav element)
    const headerNav = document.querySelector('.header__nav');

    let html = '';

    if (headerNav) {
      headerNav.querySelectorAll('.nav__item').forEach(item => {
        const topLink = item.querySelector(':scope > .nav__link');
        if (!topLink) return;

        // Check if this item has a mega-menu dropdown
        const megaMenu = item.querySelector('.mega-menu');
        const hasChildren = megaMenu && megaMenu.querySelectorAll('.mega-menu__col-heading').length > 0;

        if (hasChildren) {
          // Parent item with expandable submenu
          html += `<div class="mobile-menu__group">`;
          html += `<button class="mobile-menu__item mobile-menu__item--parent" data-mobile-submenu-toggle>
                     <span>${topLink.textContent.trim()}</span>
                     <svg class="mobile-menu__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                   </button>`;
          html += `<div class="mobile-menu__submenu">`;

          // "View All" link for the parent category
          html += `<a href="${topLink.href}" class="mobile-menu__item mobile-menu__item--sub mobile-menu__item--view-all">View All ${topLink.textContent.trim()}</a>`;

          // Child category links
          megaMenu.querySelectorAll('.mega-menu__col').forEach(col => {
            const heading = col.querySelector('.mega-menu__col-heading');
            if (!heading) return;
            html += `<a href="${heading.href}" class="mobile-menu__item mobile-menu__item--sub">${heading.textContent.trim()}</a>`;

            // Grandchild links
            col.querySelectorAll('.mega-menu__link').forEach(grandchild => {
              html += `<a href="${grandchild.href}" class="mobile-menu__item mobile-menu__item--grandchild">${grandchild.textContent.trim()}</a>`;
            });
          });

          html += `</div></div>`;
        } else {
          // Simple top-level link (no children)
          html += `<a href="${topLink.href}" class="mobile-menu__item">${topLink.textContent.trim()}</a>`;
        }
      });
    }

    // Fallback: if no nav links were found (e.g., menu not configured yet), show helpful defaults
    if (!html) {
      html = `
        <a href="/" class="mobile-menu__item">Home</a>
        <a href="/collections" class="mobile-menu__item">Shop All</a>
        <a href="/collections/beds" class="mobile-menu__item">Beds</a>
        <a href="/collections/mattresses" class="mobile-menu__item">Mattresses</a>
        <a href="/collections/ottoman-beds" class="mobile-menu__item">Ottoman Beds</a>
        <a href="/collections/divan-beds" class="mobile-menu__item">Divan Beds</a>
        <a href="/pages/about" class="mobile-menu__item">About Us</a>
        <a href="/blogs/sleep-guide" class="mobile-menu__item">Sleep Guide</a>
        <a href="/pages/contact" class="mobile-menu__item">Contact</a>
      `;
    }

    nav.innerHTML = html;

    // Submenu toggle handlers
    nav.querySelectorAll('[data-mobile-submenu-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const group = btn.closest('.mobile-menu__group');
        const submenu = group.querySelector('.mobile-menu__submenu');
        const isOpen = group.classList.contains('is-open');

        // Close all other open submenus
        nav.querySelectorAll('.mobile-menu__group.is-open').forEach(g => {
          if (g !== group) {
            g.classList.remove('is-open');
            const sub = g.querySelector('.mobile-menu__submenu');
            if (sub) sub.style.maxHeight = null;
          }
        });

        // Toggle this submenu
        group.classList.toggle('is-open', !isOpen);
        if (!isOpen && submenu) {
          submenu.style.maxHeight = submenu.scrollHeight + 'px';
        } else if (submenu) {
          submenu.style.maxHeight = null;
        }
      });
    });
  }

  // ==========================================
  // FILTER TOGGLE (mobile)
  // ==========================================
  function initFilterToggle() {
    const toggle = document.getElementById('FilterToggle');
    const sidebar = document.getElementById('FilterSidebar');
    if (!toggle || !sidebar) return;

    toggle.addEventListener('click', () => {
      sidebar.classList.toggle('filter-sidebar--open');
      toggle.classList.toggle('active');

      if (window.innerWidth <= 1024) {
        sidebar.style.display = sidebar.style.display === 'none' ? 'block' : 'none';
      }
    });

    // Collapsible filter groups
    document.querySelectorAll('.filter-group__title').forEach(title => {
      title.addEventListener('click', () => {
        const group = title.closest('.filter-group');
        group.classList.toggle('collapsed');
        const options = group.querySelector('.filter-group__options, .filter-swatches, .filter-sizes, .price-range');
        if (options) options.style.display = group.classList.contains('collapsed') ? 'none' : '';
      });
    });
  }

  // ==========================================
  // CART PAGE
  // ==========================================
  function initCartPage() {
    const cartPage = document.querySelector('[data-cart-page]');
    if (!cartPage) return;

    const loading = document.getElementById('CartLoading');

    function showLoading() { if (loading) loading.setAttribute('aria-hidden', 'false'); }
    function hideLoading() { if (loading) loading.setAttribute('aria-hidden', 'true'); }

    // --- Quantity +/- buttons ---
    cartPage.addEventListener('click', (e) => {
      const minus = e.target.closest('[data-cart-qty-minus]');
      const plus = e.target.closest('[data-cart-qty-plus]');
      if (!minus && !plus) return;

      const qtyWrap = (minus || plus).closest('[data-cart-qty]');
      const input = qtyWrap.querySelector('[data-cart-qty-input]');
      if (!input) return;

      let val = parseInt(input.value) || 1;
      if (minus) val = Math.max(0, val - 1);
      if (plus) val = Math.min(99, val + 1);
      input.value = val;

      const key = input.getAttribute('data-key');
      updateCartPageItem(key, val);
    });

    // --- Quantity input change ---
    cartPage.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cart-qty-input]');
      if (!input) return;
      const key = input.getAttribute('data-key');
      let val = parseInt(input.value) || 0;
      val = Math.max(0, Math.min(99, val));
      input.value = val;
      updateCartPageItem(key, val);
    });

    // --- Remove buttons ---
    cartPage.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cart-remove]');
      if (!btn) return;
      e.preventDefault();
      const key = btn.getAttribute('data-cart-remove');
      updateCartPageItem(key, 0);
    });

    // --- Update cart via AJAX ---
    async function updateCartPageItem(key, quantity) {
      showLoading();

      // Mark the item row as updating
      const itemRow = cartPage.querySelector(`[data-key="${key}"]`);
      if (itemRow) itemRow.classList.add('is-updating');

      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity })
        });
        const cart = await res.json();

        // Update header cart count
        updateCartCount(cart.item_count);

        // Dispatch event so cart drawer stays in sync
        document.dispatchEvent(new CustomEvent('bedzy:cart-updated'));

        // Reload page to reflect server-side cart changes
        // (This is the simplest, most reliable approach for the full cart page)
        if (quantity === 0 || cart.item_count === 0) {
          // Item removed or cart now empty — full reload
          window.location.reload();
        } else {
          // Update totals and quantities inline via AJAX
          await refreshCartPageInline(cart);
        }
      } catch (err) {
        console.error('Cart update error:', err);
        alert('There was an error updating your cart. Please try again.');
      } finally {
        hideLoading();
        if (itemRow) itemRow.classList.remove('is-updating');
      }
    }

    // --- Inline refresh (update prices/quantities without full reload) ---
    async function refreshCartPageInline(cart) {
      // Update count
      const countEl = document.getElementById('CartPageCount');
      if (countEl) countEl.textContent = cart.item_count;

      // Update subtotal & grand total
      const subtotalEl = document.getElementById('CartSubtotal');
      const grandTotalEl = document.getElementById('CartGrandTotal');
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
      if (grandTotalEl) grandTotalEl.textContent = formatMoney(cart.total_price);

      // Update shipping progress
      const threshold = window.Bedzy?.freeShippingThreshold || 300;
      const totalPounds = cart.total_price / 100;
      const progress = Math.min((totalPounds / threshold) * 100, 100);
      const shipFill = document.getElementById('CartShipFill');
      const shipText = document.getElementById('CartShipText');
      const shipEl = document.getElementById('CartShipping');

      if (shipFill) shipFill.style.width = progress + '%';
      if (shipText) {
        if (totalPounds >= threshold) {
          shipText.innerHTML = '🎉 <strong>You\'ve unlocked FREE delivery!</strong>';
          shipText.classList.add('cart-shipping__text--success');
        } else {
          const remaining = (threshold - totalPounds).toFixed(2);
          shipText.innerHTML = `🚚 Add <strong>£${remaining}</strong> more for <strong>FREE delivery</strong>`;
          shipText.classList.remove('cart-shipping__text--success');
        }
      }
      if (shipEl) {
        shipEl.textContent = totalPounds >= threshold ? 'FREE' : 'Calculated at checkout';
        shipEl.className = totalPounds >= threshold ? 'cart-summary__free' : 'cart-summary__calc';
      }

      // Update each line item
      cart.items.forEach(item => {
        const row = cartPage.querySelector(`[data-key="${item.key}"]`);
        if (!row) return;

        // Update all quantity inputs for this item
        row.querySelectorAll('[data-cart-qty-input]').forEach(input => {
          input.value = item.quantity;
        });

        // Update line total
        const totalEl = row.querySelector('[data-cart-line-total]');
        if (totalEl) totalEl.textContent = formatMoney(item.final_line_price);
      });
    }

    // --- Discount code toggle ---
    const discountToggle = document.getElementById('DiscountToggle');
    const discountForm = document.getElementById('DiscountForm');
    if (discountToggle && discountForm) {
      discountToggle.addEventListener('click', () => {
        const isOpen = discountForm.style.display !== 'none';
        discountForm.style.display = isOpen ? 'none' : 'block';
        discountToggle.classList.toggle('is-open', !isOpen);
      });
    }

    // --- Apply discount code ---
    const discountApplyBtn = document.getElementById('DiscountApplyBtn');
    const discountInput = document.getElementById('DiscountCodeInput');
    const discountMsg = document.getElementById('DiscountMsg');

    if (discountApplyBtn && discountInput) {
      discountApplyBtn.addEventListener('click', async () => {
        const code = discountInput.value.trim();
        if (!code) {
          if (discountMsg) {
            discountMsg.textContent = 'Please enter a discount code.';
            discountMsg.className = 'cart-summary__discount-msg cart-summary__discount-msg--error';
          }
          return;
        }

        discountApplyBtn.disabled = true;
        discountApplyBtn.textContent = 'Applying...';

        try {
          // Shopify applies discount codes via the checkout URL
          // We redirect to apply and come back
          window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/cart`;
        } catch (err) {
          if (discountMsg) {
            discountMsg.textContent = 'Could not apply code. Please try at checkout.';
            discountMsg.className = 'cart-summary__discount-msg cart-summary__discount-msg--error';
          }
          discountApplyBtn.disabled = false;
          discountApplyBtn.textContent = 'Apply';
        }
      });

      // Enter key to submit
      discountInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          discountApplyBtn.click();
        }
      });
    }

    // --- Save order note ---
    const noteInput = document.getElementById('CartNote');
    if (noteInput) {
      let noteTimer;
      noteInput.addEventListener('input', () => {
        clearTimeout(noteTimer);
        noteTimer = setTimeout(async () => {
          try {
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ note: noteInput.value })
            });
          } catch (err) {
            console.error('Note save error:', err);
          }
        }, 800);
      });
    }
  }

  // ==========================================
  // INITIALIZE
  // ==========================================
  document.addEventListener('DOMContentLoaded', () => {
    initOverlays();
    initPredictiveSearch();
    initQuickView();
    initProductPage();
    initProductTabs();
    initQuiz();
    initAnimations();
    initMobileMenu();
    initFilterToggle();
    initCartPage();
  });

  // Listen for cart updates
  document.addEventListener('bedzy:cart-updated', refreshCartDrawer);

})();
